import { useEffect, useState, useRef } from "react";
import { createStore } from 'polotno/model/store';
import { PolotnoContainer, SidePanelWrap, WorkspaceWrap } from 'polotno';
import { Toolbar } from 'polotno/toolbar/toolbar';
import { ZoomButtons } from 'polotno/toolbar/zoom-buttons';
import { SidePanel } from 'polotno/side-panel';
import { Workspace } from 'polotno/canvas/workspace';
import { Button, Divider } from '@blueprintjs/core';
import { Cross } from "@blueprintjs/icons";
import { globalStore} from "./components/globalStore";
import { TemplatesPanel } from "./components/templates";
import { AssetsSection } from "./components/assets";
import { TemplateName } from "./components/templateName";
import { TemplateSaveButton } from "./components/templateSaveButton";
import TemplateDownloadButton from "./components/templateDownloadButton";
import fontsFunctions from "./fontsFunctions";

import { TextSection, ElementsSection, TemplatesSection, LayersSection, SizeSection } from 'polotno/side-panel';

import '@blueprintjs/core/lib/css/blueprint.css';

const Designer = ({options ,onSaveImage, onClose}) => {
    const { apiKey, template, imageApiEndpoint, apiEndpoint } = options;
    const [userRole, setUserRole] = useState('user');
    const [sections, setSections] = useState([
        AssetsSection,
        TextSection,
        ElementsSection
    ]);

    const store = useRef(createStore({
        key: 'YzDDTxVKl0nzwkoZcdVb',
        showCredit: false
    })).current;

    const loadTemplate = async() => {
        let req = `${globalStore.serverUrl}/cloudtemplates/${template}`;

        if(template.toLowerCase().indexOf(imageApiEndpoint)===0){
            req = template.toLowerCase().replace(/\/image(?=\?)/, '/data');
        }

        const res = await fetch(req, {
            method: "GET",
            headers: {
            "Content-Type": "application/json",
            ...(globalStore.apiKey ? {"x-api-key": globalStore.apiKey} : {}),
            }
        });
        const json = await res.json();
        store.loadJSON(json.content);
        globalStore.template = json;
        globalStore.contentChanged = false;
    }

    const saveImage = async () => {
        try {
            const templateContent = store.toJSON(); 
            const generateUrl = (page) => {
                let url = template.split("image?")[0] + "image?";
                page.children.forEach((layer) => {
                    const layerName = layer.name ? layer.name : layer.id;
                    if (layer.type === 'text' && layer.text) {
                        url += `${layerName}=${layer.text}&`;
                    }
                    if (layer.type === 'figure' && layer.fill) {
                        url += `${layerName}=${layer.fill}&`;
                    }
                    if (layer.type === 'image' && layer.src) {
                        url += `${layerName}=${layer.src}&`;
                    }
                });
                if (url.endsWith('&')) {
                    url = url.slice(0, -1);
                }
                return encodeURI(url);
            };
    
            const firstPage = templateContent.pages[0];
            const url = generateUrl(firstPage);
            onSaveImage(url);  
        } catch (err) {
            console.error("Error generating image URL:", err);
        }
    };


    const handleClose = () => {
        if(globalStore.contentChanged && !window.confirm("All changes will be lost! Are you sure?")) return;
        onClose();
    }

    const CustomControls = ({ store}) => {
        return (
            <div style={{display: "flex"}}>
                <TemplateDownloadButton store={store}/>
                <Divider style={{visibility: "hidden"}}/>
                {globalStore.userRole === 'admin' && <TemplateSaveButton store={store}/>}
            </div>
        );
    };

    const determineUserRole = (role) => {
        if (role === 'admin' || role === 'manager') {
            globalStore.userRole = 'admin';
        } else {
            globalStore.userRole = 'user';
        }

    };

    const fetchUserData = async () => {
        const url = `${globalStore.serverUrl}/user/me`;
        const res = await fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                ...(globalStore.apiKey ? { "x-api-key": globalStore.apiKey } : {}),
            }
        });
        const user = await res.json();
        const role = user?.farms[0]?.role;
        determineUserRole(role);
        store.setRole(globalStore.userRole);
        setUserRole(globalStore.userRole);
    };

    useEffect(() => {
        const init = async () => {
            globalStore.serverUrl = apiEndpoint;
            globalStore.apiKey = apiKey;
            globalStore.contentChanged = false;
            globalStore.template = {
                name: 'New Template'
            };
    
            store.addPage();
            TemplatesSection.Panel = TemplatesPanel;
    
            const unsubscribeKey = store.on('change', (updatedStore)=>{
                fontsFunctions.manageFonts(updatedStore.fonts);
                globalStore.contentChanged = true;
            });
    
            if (template) {
                await loadTemplate();
            }
    
            await fontsFunctions.setFontsGlobally().then((fonts) => {
                fonts.forEach(font => {
                    store.addFont(font);
                });
                globalStore.fontsInProgress = false;
            });
    
            await fetchUserData();
    
            setSections((prevSections) => {
                let newSections = [...prevSections];
                if (globalStore.userRole === 'admin' && !newSections.includes(SizeSection)) {
                    newSections.push(SizeSection);
                }
                if (!template && !newSections.includes(TemplatesSection)) {
                    newSections.unshift(TemplatesSection);
                }
                return newSections;
            });
            return unsubscribeKey;
        };
    
        init().catch(console.error);
    
    }, [template]);

    return (

        <PolotnoContainer className="flex flex-col bg-white ">
            {onSaveImage && <div className="py-2 pl-1 pr-5 flex items-center justify-between border border-t-gray-300 bg-gray-100">
                <a href="https://www.templatefarm.io" target="_blank" rel="noreferrer" className="outline-none">
                    <img className="h-8" src='https://d2f9eheulyqk7v.cloudfront.net/internal/te-logo.svg' alt='TELogo'/>
                </a>
                <div className="flex">
                    <Cross className="opacity-50 hover:opacity-100 cursor-pointer" onClick={handleClose}/>
                </div>
            </div>}
            <div className="flex border border-b-gray-200">
                <div className="flex items-center w-auto bp5-navbar">
                    <TemplateName store={globalStore} polotnoStore={store}/>
                </div>
                <Toolbar store={store} components={{ActionControls: CustomControls}}/>
            </div>
            <div className="flex flex-auto overflow-y-hidden">
                <SidePanelWrap>
                    <SidePanel store={store} sections={sections} defaultSection="templates" />
                </SidePanelWrap>
                <WorkspaceWrap>

                    <Workspace store={store} />
                    <ZoomButtons store={store} />
                </WorkspaceWrap>
                <div className="p-1 min-w-80">
                    <LayersSection.Panel store={store} />
                </div>
            </div>
            {onSaveImage && <div className="py-2 px-3 flex justify-between border border-t-gray-300 bg-gray-100">
                <div className="flex">
                </div>
                <div className="flex">
                    <Button intent="primary" icon="circle-arrow-down" onClick={saveImage}>Save</Button>
                </div>

            </div>}
        </PolotnoContainer>

  );
}

export default Designer

